/**
 * Language Switcher Accessibility Enhancement
 * Adds keyboard navigation and ARIA support to Langify language switcher components
 */

document.addEventListener('DOMContentLoaded', function() {
    // Wait for Langify to initialize
    setTimeout(function() {
        enhanceLanguageSwitcherAccessibility();
    }, 200);
});

function enhanceLanguageSwitcherAccessibility() {
    // Enhance custom dropdown switchers
    enhanceCustomDropdownSwitchers();
    
    // Enhance native select elements
    enhanceNativeSelectSwitchers();
    
    // Enhance popup switchers
    enhancePopupSwitchers();
    
    // Enhance link switchers
    enhanceLinkSwitchers();
    
    // Enhance close buttons
    enhanceCloseButtons();
}

function enhanceCustomDropdownSwitchers() {
    const customDropdowns = document.querySelectorAll('.ly-custom-dropdown-switcher');
    
    customDropdowns.forEach(function(dropdown) {
        const currentButton = dropdown.querySelector('.ly-custom-dropdown-current');
        const dropdownList = dropdown.querySelector('.ly-custom-dropdown-list');
        const listItems = dropdown.querySelectorAll('.ly-custom-dropdown-list li');
        
        if (!currentButton || !dropdownList) return;
        
        // Make sure the button is focusable
        if (!currentButton.hasAttribute('tabindex')) {
            currentButton.setAttribute('tabindex', '0');
        }
        
        // Add keyboard event listener to the dropdown button
        currentButton.addEventListener('keydown', function(e) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                // Trigger the existing onclick handler
                if (dropdown.onclick) {
                    dropdown.onclick.call(dropdown, e);
                } else if (window.langify && window.langify.switcher && window.langify.switcher.toggleSwitcherOpen) {
                    window.langify.switcher.toggleSwitcherOpen(dropdown);
                }
                
                // Focus first item when dropdown opens
                setTimeout(function() {
                    const firstLink = dropdown.querySelector('.ly-custom-dropdown-list a');
                    if (firstLink && dropdown.classList.contains('ly-is-open')) {
                        firstLink.focus();
                    }
                }, 50);
            } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                // Open dropdown and focus first/last item
                if (!dropdown.classList.contains('ly-is-open')) {
                    if (dropdown.onclick) {
                        dropdown.onclick.call(dropdown, e);
                    }
                    setTimeout(function() {
                        const links = dropdown.querySelectorAll('.ly-custom-dropdown-list a');
                        if (links.length > 0) {
                            if (e.key === 'ArrowDown') {
                                links[0].focus();
                            } else {
                                links[links.length - 1].focus();
                            }
                        }
                    }, 50);
                }
            } else if (e.key === 'Escape') {
                e.preventDefault();
                if (dropdown.classList.contains('ly-is-open') && dropdown.onclick) {
                    dropdown.onclick.call(dropdown, e);
                }
            }
        });
        
        // Enhance dropdown list items
        const links = dropdown.querySelectorAll('.ly-custom-dropdown-list a');
        links.forEach(function(link, index) {
            // Make links focusable
            link.setAttribute('tabindex', '0');
            
            // Add keyboard navigation within dropdown
            link.addEventListener('keydown', function(e) {
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    const nextLink = links[index + 1] || links[0];
                    nextLink.focus();
                } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    const prevLink = links[index - 1] || links[links.length - 1];
                    prevLink.focus();
                } else if (e.key === 'Escape') {
                    e.preventDefault();
                    if (dropdown.onclick) {
                        dropdown.onclick.call(dropdown, e);
                    }
                    currentButton.focus();
                } else if (e.key === 'Tab') {
                    // Allow normal tab behavior, but close dropdown when tabbing out
                    setTimeout(function() {
                        if (!dropdown.contains(document.activeElement)) {
                            dropdown.classList.remove('ly-is-open');
                            currentButton.setAttribute('aria-expanded', 'false');
                        }
                    }, 10);
                }
            });
            
            // Close dropdown when a link is activated
            link.addEventListener('click', function() {
                dropdown.classList.remove('ly-is-open');
                currentButton.setAttribute('aria-expanded', 'false');
            });
        });
        
        // Update ARIA attributes when dropdown state changes
        const observer = new MutationObserver(function(mutations) {
            mutations.forEach(function(mutation) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    const isOpen = dropdown.classList.contains('ly-is-open');
                    currentButton.setAttribute('aria-expanded', isOpen.toString());
                }
            });
        });
        
        observer.observe(dropdown, { attributes: true, attributeFilter: ['class'] });
        
        // Set initial ARIA state
        const isOpen = dropdown.classList.contains('ly-is-open');
        currentButton.setAttribute('aria-expanded', isOpen.toString());
    });
}

function enhanceNativeSelectSwitchers() {
    const nativeSelects = document.querySelectorAll('.ly-native-select');
    
    nativeSelects.forEach(function(select) {
        // Native selects are already keyboard accessible, just ensure they have proper labels
        if (!select.hasAttribute('aria-label') && !select.hasAttribute('aria-labelledby')) {
            if (select.classList.contains('ly-languages-switcher')) {
                select.setAttribute('aria-label', 'Language selector');
            } else if (select.classList.contains('ly-currency-switcher')) {
                select.setAttribute('aria-label', 'Currency selector');
            } else if (select.classList.contains('ly-country-switcher')) {
                select.setAttribute('aria-label', 'Country selector');
            }
        }
    });
}

function enhancePopupSwitchers() {
    const popupSwitchers = document.querySelectorAll('.ly-popup-switcher');
    
    popupSwitchers.forEach(function(popup) {
        const trigger = popup.querySelector('.ly-popup-current');
        const modal = popup.querySelector('.ly-popup-modal');
        const closeButton = popup.querySelector('.ly-close');
        
        if (!trigger) return;
        
        // Make trigger focusable
        if (!trigger.hasAttribute('tabindex')) {
            trigger.setAttribute('tabindex', '0');
        }
        
        trigger.setAttribute('role', 'button');
        trigger.setAttribute('aria-label', 'Open language selector');
        
        // Add keyboard event listener to trigger
        trigger.addEventListener('keydown', function(e) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (trigger.onclick) {
                    trigger.onclick.call(trigger, e);
                } else if (window.langify && window.langify.switcher && window.langify.switcher.togglePopupOpen) {
                    window.langify.switcher.togglePopupOpen(trigger);
                }
            }
        });
        
        // Trap focus within modal when open
        if (modal) {
            const handleModalKeydown = function(e) {
                if (e.key === 'Escape') {
                    e.preventDefault();
                    if (closeButton && closeButton.onclick) {
                        closeButton.onclick.call(closeButton, e);
                    }
                    trigger.focus();
                } else if (e.key === 'Tab') {
                    // Simple focus trap - cycle through focusable elements in modal
                    const focusableElements = modal.querySelectorAll('a, button, [tabindex="0"]');
                    const firstElement = focusableElements[0];
                    const lastElement = focusableElements[focusableElements.length - 1];
                    
                    if (e.shiftKey && document.activeElement === firstElement) {
                        e.preventDefault();
                        lastElement.focus();
                    } else if (!e.shiftKey && document.activeElement === lastElement) {
                        e.preventDefault();
                        firstElement.focus();
                    }
                }
            };
            
            modal.addEventListener('keydown', handleModalKeydown);
            
            // Focus first element when modal opens
            const observer = new MutationObserver(function(mutations) {
                mutations.forEach(function(mutation) {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                        if (popup.classList.contains('ly-is-open')) {
                            setTimeout(function() {
                                const firstFocusable = modal.querySelector('a, button, [tabindex="0"]');
                                if (firstFocusable) {
                                    firstFocusable.focus();
                                }
                            }, 50);
                        }
                    }
                });
            });
            
            observer.observe(popup, { attributes: true, attributeFilter: ['class'] });
        }
    });
}

function enhanceLinkSwitchers() {
    const linkSwitchers = document.querySelectorAll('.ly-languages-switcher.ly-links a, .ly-languages-switcher.ly-list a');
    
    linkSwitchers.forEach(function(link) {
        // Make sure links are focusable
        if (!link.hasAttribute('tabindex')) {
            link.setAttribute('tabindex', '0');
        }
        
        // Add keyboard activation
        link.addEventListener('keydown', function(e) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                link.click();
            }
        });
    });
}

function enhanceCloseButtons() {
    const closeButtons = document.querySelectorAll('.ly-close');
    
    closeButtons.forEach(function(button) {
        // Make close buttons focusable
        if (!button.hasAttribute('tabindex')) {
            button.setAttribute('tabindex', '0');
        }
        
        button.setAttribute('role', 'button');
        button.setAttribute('aria-label', 'Close');
        
        // Add keyboard activation
        button.addEventListener('keydown', function(e) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (button.onclick) {
                    button.onclick.call(button, e);
                }
            }
        });
    });
}

// Handle clicks outside dropdown to close them
document.addEventListener('click', function(e) {
    const openDropdowns = document.querySelectorAll('.ly-custom-dropdown-switcher.ly-is-open');
    openDropdowns.forEach(function(dropdown) {
        if (!dropdown.contains(e.target)) {
            dropdown.classList.remove('ly-is-open');
            const button = dropdown.querySelector('.ly-custom-dropdown-current');
            if (button) {
                button.setAttribute('aria-expanded', 'false');
            }
        }
    });
});